import { Store } from '../../../store';
import {
    Component,
    ComponentFactoryResolver,
    Input,
    OnChanges,
    OnInit,
    SimpleChange,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { Router } from '@angular/router';

@Component({
    selector: 'app-plugin-loader',
    templateUrl: './plugin-loader.component.html',
    styleUrls: ['./plugin-loader.component.scss']
})
export class PluginLoaderComponent implements OnInit, OnChanges {
    @Input() page;
    @Input() snippets;
    @Input() hasLoaded = false;
    deviceType$ = this.store.select<string>('deviceType');
    deviceType = this.store.selectForLocal('deviceType');
    currentUrl = '';
    showFooter = false; // this could be done via store but to test it now

    @ViewChild('loaderContainer', { read: ViewContainerRef, static: false }) viewContainerRef: ViewContainerRef;

    constructor(
        private store: Store,
        private componentFactoryResolver: ComponentFactoryResolver,
        private router: Router,
    ) {
    }

    ngOnInit(): void {
        this.currentUrl = this.router.url;
    }

    ngOnChanges(changes: SimpleChanges) {
        const currentSnippets: SimpleChange = changes.snippets;
        if (currentSnippets.currentValue && currentSnippets.currentValue.length > 0 && this.hasLoaded) {
            this.loadComponents();
        } else {
            if (currentSnippets.currentValue.length === 0 && this.hasLoaded) {
                this.loadPageNotFound();
            }
        }
    }

    createDynamicComponent<T>(component: Type<T>) {
        const factory = this.componentFactoryResolver.resolveComponentFactory(component);
        return this.viewContainerRef.createComponent(factory);
    }

    async loadComponents() {
        if (this.viewContainerRef) {
            this.viewContainerRef.clear();
        }

        if (this.page && this.page.introTitle) {
            const { PageIntroComponent } = await import('../../../shared/page-intro/page-intro.component');
            const componentRef = this.createDynamicComponent(PageIntroComponent);
            componentRef.instance.backgroundImg = this.page.introBackground;
            componentRef.instance.title = this.page.introTitle;
            componentRef.instance.text = this.page.introText;
        }

        for (const snippet of this.snippets) {
            if (snippet.type === 'image') {
                const { ImageComponent } = await import('../../../cms/plugins/image/image.component');
                const componentRef = this.createDynamicComponent(ImageComponent);
                componentRef.instance.snippet = snippet;
            }

            if (snippet.type === 'wysiwyg') {
                const { WysiwygComponent } = await import('../../../cms/plugins/wysiwyg/wysiwyg.component');
                const componentRef = this.createDynamicComponent(WysiwygComponent);
                componentRef.instance.snippet = snippet;
            }

            if (snippet.type === 'grid') {
                const { CmsGridComponent } = await import('../../../cms/plugins/cms-grid/cms-grid.component');
                const componentRef = this.createDynamicComponent(CmsGridComponent);
                componentRef.instance.snippet = snippet;
            }

            if (snippet.type === 'plugin') {
                if (snippet.content === 'brand-video') {
                    const { BrandVideoPluginComponent } = await import('../../../cms/plugins/brand-video-plugin/brand-video-plugin.component');
                    const componentRef = this.createDynamicComponent(BrandVideoPluginComponent);
                    componentRef.instance.snippet = snippet;
                }

                if (snippet.content === 'contact-form') {
                    const { ContactFormComponent } = await import('../../../cms/plugins/contact-form/contact-form.component');
                    const componentRef = this.createDynamicComponent(ContactFormComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'product-type-slider') {
                    const { ProductTypesSliderComponent } = await import('../../../cms/plugins/product-types-slider/product-types-slider.component');
                    const componentRef = this.createDynamicComponent(ProductTypesSliderComponent);
                    // Todo fix homepage in admin area
                    componentRef.instance.backgroundColour = 'bg-white';
                }

                // Todo ability to set different image sources for same slide in admin backend
                if (snippet.content === 'gallery' && snippet.gallery === 'homepage' && (this.deviceType === 'desktop')) {
                    const { GalleryHomepageComponent } = await import('../../../cms/plugins/gallery-homepage/gallery-homepage.component');
                    const componentRef = this.createDynamicComponent(GalleryHomepageComponent);
                    componentRef.instance.galleryId = snippet.gallery;
                }

                if (snippet.content === 'gallery' && snippet.gallery === '2a594afd-8e12-40d3-ad1b-1898759b1e2f' && this.deviceType === 'mobile') {
                    const { GalleryHomepageComponent } = await import('../../../cms/plugins/gallery-homepage/gallery-homepage.component');
                    const componentRef = this.createDynamicComponent(GalleryHomepageComponent);
                    componentRef.instance.galleryId = snippet.gallery;
                }

                if (snippet.content === 'gallery' && snippet.gallery === '47cdee3c-31eb-4c47-ada0-2866016b26bd' && (this.deviceType === 'tabletV' || this.deviceType === 'tabletH')) {
                    const { GalleryHomepageComponent } = await import('../../../cms/plugins/gallery-homepage/gallery-homepage.component');
                    const componentRef = this.createDynamicComponent(GalleryHomepageComponent);
                    componentRef.instance.galleryId = snippet.gallery;
                }

                if (snippet.content === 'meet-the-team') {
                    const { MeetTheTeamComponent } = await import('../../../cms/plugins/meet-the-team/meet-the-team.component');
                    const componentRef = this.createDynamicComponent(MeetTheTeamComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'case-studies') {
                    const { CaseStudyPluginComponent } = await import('../../../cms/plugins/case-study-plugin/case-study-plugin.component');
                    const componentRef = this.createDynamicComponent(CaseStudyPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'faq') {
                    const { FaqPluginComponent } = await import('../../../cms/plugins/faq-plugin/faq-plugin.component');
                    const componentRef = this.createDynamicComponent(FaqPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'advice') {
                    const { AdvicePluginComponent } = await import('../../../cms/plugins/advice-plugin/advice-plugin.component');
                    const componentRef = this.createDynamicComponent(AdvicePluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'product-group') {
                    const { ProductTypesPluginComponent } = await import('../../../cms/plugins/product-types-plugin/product-types-plugin.component');
                    const componentRef = this.createDynamicComponent(ProductTypesPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'product-ranges') {
                    const { ProductRangesPluginComponent } = await import('../../../cms/plugins/product-ranges-plugin/product-ranges-plugin.component');
                    const componentRef = this.createDynamicComponent(ProductRangesPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'product-groups') {
                    const { ProductGroupsPluginComponent } = await import('../../../cms/plugins/product-groups-plugin/product-groups-plugin.component');
                    const componentRef = this.createDynamicComponent(ProductGroupsPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'quality-etched') {
                    const { QualityEtchedComponent } = await import('../../../cms/plugins/quality-etched/quality-etched.component');
                    const componentRef = this.createDynamicComponent(QualityEtchedComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'special-offers') {
                    const { SpecialOffersPluginComponent } = await import('../../../cms/plugins/special-offers-plugin/special-offers-plugin.component');
                    const componentRef = this.createDynamicComponent(SpecialOffersPluginComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                    // Fixme unpretty, we should control this via CMS instead of hardcoded condition
                    if (this.currentUrl === '/' || (this.currentUrl.charAt(0) === '/' && this.currentUrl.includes('/?'))) {
                        componentRef.instance.showHeading = true;
                    } else {
                        componentRef.instance.noSlider = true;
                    }
                }

                if (snippet.content === 'blog-categories') {
                    const { BlogCategoriesComponent } = await import('../../../cms/plugins/blog-categories/blog-categories.component');
                    const componentRef = this.createDynamicComponent(BlogCategoriesComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'case-study-preview') {
                    const { PromoCaseStudyComponent } = await import('../../../cms/plugins/promo-case-study/promo-case-study.component');
                    const componentRef = this.createDynamicComponent(PromoCaseStudyComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'contact-cta') {
                    const { ContactCtaComponent } = await import('../../../shared/contact-cta/contact-cta.component');
                    const componentRef = this.createDynamicComponent(ContactCtaComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'trustpilot-slider') {
                    const { TrustpilotReviewSliderComponent } = await import('../../../shared/trustpilot-review-slider/trustpilot-review-slider.component');
                    this.createDynamicComponent(TrustpilotReviewSliderComponent);
                }

                if (snippet.content === 'gallery' && snippet.gallery === '5e6031ea-f8a2-4593-8512-42765fc3da75') {
                    const { PartnerGalleryComponent } = await import('../../../cms/plugins/partner-gallery/partner-gallery.component');
                    const componentRef = this.createDynamicComponent(PartnerGalleryComponent);
                    componentRef.instance.galleryId = snippet.gallery;
                }

                if (snippet.content === 'customer-comfort' && (this.deviceType === 'mobile' || this.deviceType === 'desktop')) {
                    const { CustomerComfortComponent } = await import('../../../cms/plugins/customer-comfort/customer-comfort.component');
                    const componentRef = this.createDynamicComponent(CustomerComfortComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'job-tiles') {
                    const { JobTilesComponent } = await import('../../../cms/plugins/job-tiles/job-tiles.component');
                    const componentRef = this.createDynamicComponent(JobTilesComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'product-favourites') {
                    const { FavouritesComponent } = await import('../../../cms/plugins/favourites/favourites.component');
                    const componentRef = this.createDynamicComponent(FavouritesComponent);
                    componentRef.instance.backgroundColour = snippet.backgroundColour;
                }

                if (snippet.content === 'tabs-terms') {
                    const { TabsTermsPluginComponent } = await import('../../../cms/plugins/tabs-terms-plugin/tabs-terms-plugin.component');
                    this.createDynamicComponent(TabsTermsPluginComponent);
                }

                if (snippet.content === 'newsletter-cta') {
                    const { NewsletterCtaComponent } = await import('../../../cms/plugins/newsletter-cta/newsletter-cta.component');
                    this.createDynamicComponent(NewsletterCtaComponent);
                }

                if (snippet.content === 'loyality-program-intro') {
                    const { LoyalityPageIntroComponent } = await import('../../../cms/plugins/loyality-page-intro/loyality-page-intro.component');
                    this.createDynamicComponent(LoyalityPageIntroComponent);
                }

                if (snippet.content === 'usp') {
                    const { UspPluginComponent } = await import('../../../cms/plugins/usp/usp.component');
                    this.createDynamicComponent(UspPluginComponent);
                }

            }
        }
        this.showFooter = true;
    }

    async loadPageNotFound() {
        if (this.viewContainerRef) {
            this.viewContainerRef.clear();
        }

        const { PageNotFoundComponent } = await import('../../../cms/page-not-found/page-not-found.component');
        this.createDynamicComponent(PageNotFoundComponent);

        const { TrustpilotReviewSliderComponent } = await import('../../../shared/trustpilot-review-slider/trustpilot-review-slider.component');
        const componentRefTrustpilotReviews = this.createDynamicComponent(TrustpilotReviewSliderComponent);
        componentRefTrustpilotReviews.instance.backgroundImage = 'https://ys-assets.b-cdn.net/cms/images/a9d510dc6d97fc3ebc10b16f19b3105d53.png';

        const { ContactCtaComponent } = await import('../../../shared/contact-cta/contact-cta.component');
        const componentRefContactCta = this.createDynamicComponent(ContactCtaComponent);
        componentRefContactCta.instance.backgroundColour = 'bg-grey';
    }
}
